/**
 * Custom hook for fetching supplies from Firestore
 */

import { useState, useEffect, useCallback } from 'react';
import { getAllSupplies, Supply } from '../services/SupplyService';

export interface UseSuppliesResult {
  supplies: Supply[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch all supplies
 */
export function useSupplies(): UseSuppliesResult {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSupplies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching supplies from Firestore...');
      const fetchedSupplies = await getAllSupplies();
      console.log(`✅ Successfully loaded ${fetchedSupplies.length} supplies`);
      
      if (fetchedSupplies.length === 0) {
        console.warn('⚠️ No supplies found in Firestore. Make sure supplies are uploaded to the Supplies collection.');
        setError('No supplies found. Please ensure supplies are uploaded to the database.');
      } else {
        setSupplies(fetchedSupplies);
      }
    } catch (err: any) {
      console.error('❌ Error fetching supplies:', err);
      // Provide more detailed error message
      let errorMessage = 'Failed to load supplies. ';
      
      if (err?.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check Firestore security rules.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage += 'Firestore index required. Please create a composite index for category and code.';
      } else if (err?.code === 'not-found') {
        errorMessage += 'Supplies collection not found. Please ensure supplies are uploaded.';
      } else if (err?.message) {
        errorMessage += `Error: ${err.message}`;
      } else {
        errorMessage += 'Please check the console for details.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSupplies();
  }, [fetchSupplies]);

  return {
    supplies,
    loading,
    error,
    refetch: fetchSupplies,
  };
}

